/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useState } from 'react';
import {
  useTable,
  useSortBy,
  usePagination,
  useFilters,
  useFlexLayout,
  TableState,
  ColumnInstance,
  UsePaginationInstanceProps,
  UseSortByInstanceProps,
  UseSortByColumnProps,
  UsePaginationState,
  UseSortByState,
  UseTableInstanceProps,
} from 'react-table';
import MonsterOpen5e from 'model/formats/MonsterOpen5e';
import CreatureOpen5e from 'model/formats/CreatureOpen5e';
import DataModel from 'model/DataModel';
import PropertySet from 'components/common/PropertySet';
import Monster from 'model/monster/Monster';
import { containsSearchCriteria, parseSearchString } from 'components/search/MonsterSearchUtils';
import { monsterGridColumns } from 'components/search/monsterGridColumns';

interface MonsterGridProps {
  onSelectMonster?: (slug: string) => void;
  minCr: number | null;
  maxCr: number | null;
  keywords: string;
  selectedEnvironments: string[];
  pageSize: number | null;
  currentPage: number | null;
  sortBy: { id: string; desc: boolean }[];
  dataModel: DataModel;
  myMonsters: Monster[];
  communityMonsters: { username: string; monster: Monster }[];
  onUpdateMinCr: (minCr: number) => void;
  onUpdateMaxCr: (maxCr: number) => void;
  onUpdateKeywords: (keywords: string) => void;
  onUpdateSelectedEnvironments: (environments: string[]) => void;
  onUpdatePageSize: (pageSize: number) => void;
  onUpdateCurrentPage: (currentPage: number) => void;
  onUpdateSort: (sortBy: { id: string; desc: boolean }[]) => void;
}

const MonsterGrid: React.FC<MonsterGridProps> = ({
  onSelectMonster, minCr, maxCr, keywords, selectedEnvironments, pageSize, currentPage, sortBy, dataModel, myMonsters, communityMonsters,
  onUpdateMinCr, onUpdateMaxCr, onUpdateKeywords, onUpdateSelectedEnvironments, onUpdatePageSize, onUpdateCurrentPage, onUpdateSort
}) => {
  const [monsterData, setMonsterData] = useState<MonsterOpen5e[]>([]);
  const [filteredMonsterData, setFilteredMonsterData] = useState<MonsterOpen5e[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadProgress, setLoadProgress] = useState<string>('Retrieving other data.');
  const [internalPageSize, setInternalPageSize] = useState<number>(pageSize || 25);
  const [environments, setEnvironments] = useState<string[]>(['Water', 'Mountains', 'Desert', 'Hills']);
  const [envOpen, setEnvOpen] = useState<boolean>(false);

  // Convert a Monster to MonsterOpen5e format for searching
  const convertMonsterToOpen5e = (monster: Monster): MonsterOpen5e => {
    const open5eMon = new MonsterOpen5e();
    open5eMon.slug = 'user_' + monster.name;
    open5eMon.name = monster.name;
    open5eMon.desc = monster.desc;
    open5eMon.size = monster.size;
    open5eMon.type = monster.type;
    open5eMon.subtype = monster.subtype;
    open5eMon.group = monster.group;
    open5eMon.alignment = monster.alignment;
    open5eMon.armor_class = monster.armor_class;
    open5eMon.armor_desc = monster.armor_desc;
    open5eMon.hit_points = monster.hit_points;
    open5eMon.hit_dice = monster.hit_dice;
    open5eMon.speed = monster.speed;
    open5eMon.strength = monster.attributes.strength;
    open5eMon.dexterity = monster.attributes.dexterity;
    open5eMon.constitution = monster.attributes.constitution;
    open5eMon.intelligence = monster.attributes.intelligence;
    open5eMon.wisdom = monster.attributes.wisdom;
    open5eMon.charisma = monster.attributes.charisma;
    open5eMon.damage_vulnerabilities = monster.damage_vulnerabilities;
    open5eMon.damage_resistances = monster.damage_resistances;
    open5eMon.damage_immunities = monster.damage_immunities;
    open5eMon.condition_immunities = monster.condition_immunities;
    open5eMon.senses = monster.senses;
    open5eMon.languages = monster.languages;
    open5eMon.challenge_rating = monster.challenge_rating;
    open5eMon.cr = monster.cr;
    open5eMon.actions = monster.actions;
    open5eMon.bonus_actions = monster.bonus_actions;
    open5eMon.reactions = monster.reactions;
    // If a monster has a legendary description, but no legendary actions, then the description does not show, so hide it.
    if (monster.legendary_actions.length == 0) {
      open5eMon.legendary_desc = '';
    } else {
      open5eMon.legendary_desc = monster.legendary_desc;
    }
    open5eMon.legendary_actions = monster.legendary_actions;
    open5eMon.special_abilities = monster.traits;
    open5eMon.environments = monster.environments;
    open5eMon.document__slug = 'personal';
    open5eMon.document__title = 'My Monsters';
    open5eMon.document__license_url = '';
    open5eMon.document__url = '';
    return open5eMon;
  };

  useEffect(() => {
    const allEnvs = monsterData.flatMap(mon => mon.environments || []);
    const uniqueEnvs = Array.from(new Set(allEnvs)).sort();
    setEnvironments(uniqueEnvs);
  }, [monsterData]);

  useEffect(() => {
    let reducedMonsters = monsterData;
    if (minCr && !isNaN(minCr)) {
      reducedMonsters = reducedMonsters.filter(mon => mon.cr >= minCr);
    }
    if (maxCr && !isNaN(maxCr)) {
      reducedMonsters = reducedMonsters.filter(mon => mon.cr <= maxCr);
    }
    if (keywords !== '') {
      const { positive: positiveWords, negative: negativeWords, fieldSpecific } = parseSearchString(keywords.trim().toLowerCase());
      reducedMonsters = reducedMonsters.filter(mon => containsSearchCriteria(mon, positiveWords, negativeWords, fieldSpecific));
    }
    const selectedEnvs = selectedEnvironments.filter(env => env && env != '--')
    if (selectedEnvs.length > 0) {
      reducedMonsters = reducedMonsters.filter(row => {
        for (const env of selectedEnvs ) {
          if (row.environments.includes(env))
            return true;
        }
        return false;
      });
    }

    const count = reducedMonsters.length;
    let newLastPage: number = Math.floor(count / internalPageSize) + 1;
    if ((currentPage ?? 0) >= newLastPage) {
      onUpdateCurrentPage(newLastPage - 1);
    }

    setFilteredMonsterData(reducedMonsters);
  }, [minCr, maxCr, keywords, selectedEnvironments, monsterData]);

  useEffect(() => {
    const fetchMonsters = async () => {
      if (dataModel?.monsters == null || dataModel.monsters.data.length === 0 || dataModel.monsters.progress ||
          dataModel?.creatures == null || dataModel.creatures.data.length === 0 || dataModel.creatures.progress) {
        if (dataModel.monsters.progress) {
          setLoadProgress(dataModel.monsters.progress);
        } else if (dataModel.creatures.progress) {
          setLoadProgress(dataModel.creatures.progress);
        }
        setLoading(true);
      } else {
        setLoadProgress('');
        setLoading(false);
      }
      const newMonsters: MonsterOpen5e[] = [];

      // Add Open5e monsters
      for (const monster of dataModel?.monsters?.data || []) {
        newMonsters.push(structuredClone(monster));
      }
      // Add Open5e creature
      for (const creature of dataModel?.creatures?.data || []) {
        newMonsters.push(CreatureOpen5e.convertToMonsterOpen5e(creature));
      }
      // Add user's monsters
      for (const userMonster of myMonsters || []) {
        newMonsters.push(convertMonsterToOpen5e(userMonster));
      }
      // Add other users' public (community) monsters, with a 'community_<ownerId>_<name>' slug so
      // selecting one loads it read-only from the community list (the owner id keeps two same-named
      // monsters from different owners distinct). The owner's username is shown as the Source.
      for (const { username, monster } of communityMonsters || []) {
        const o = convertMonsterToOpen5e(monster);
        o.slug = 'community_' + monster.ownerId + '_' + monster.name;
        o.document__slug = username || 'community';
        newMonsters.push(o);
      }

      setMonsterData(newMonsters);
      setFilteredMonsterData(newMonsters);
    };

    fetchMonsters();
  }, [dataModel, dataModel.monsters, dataModel.monsters.data, dataModel.monsters.progress,
                 dataModel.creatures, dataModel.creatures.data, dataModel.creatures.progress,
      myMonsters, communityMonsters]);

  useEffect(() => {
    if (pageSize !== null) {
      setInternalPageSize(pageSize);
    }
  }, [pageSize]);

  useEffect(() => {
    onUpdatePageSize(internalPageSize);
  }, [internalPageSize]);

  const crOptions = [
    { value: 0, label: "0" },
    { value: 0.125, label: "1/8" },
    { value: 0.25, label: "1/4" },
    { value: 0.5, label: "1/2" },
    ...Array.from({ length: 31 }, (v, i) => ({ value: i, label: String(i) }))
  ];

  const columns = monsterGridColumns;

  const initialState: Partial<TableState<MonsterOpen5e>> & Partial<UsePaginationState<MonsterOpen5e>> & Partial<UseSortByState<MonsterOpen5e>> = {
    pageIndex: currentPage ?? 0,  // From UsePaginationState
    pageSize: internalPageSize,  // From UsePaginationState
    sortBy: sortBy || [{ id: 'name', desc: false }],  // From UseSortByState
  };

  // Define the correct type for the table instance
  type TableInstanceWithPlugins<T extends object> = UseTableInstanceProps<T> &
    UsePaginationInstanceProps<T> &
    UseSortByInstanceProps<T>;

  type SortableColumnInstance<T extends object> = ColumnInstance<T> & UseSortByColumnProps<T>;
  type TableStateWithPagination<T extends object> = TableState<T> & UsePaginationState<T>;

  const tableInstance: TableInstanceWithPlugins<MonsterOpen5e> = useTable<MonsterOpen5e>(
    { columns, data: filteredMonsterData, initialState },
    useFilters,
    useSortBy,
    usePagination,
    useFlexLayout
  ) as unknown as TableInstanceWithPlugins<MonsterOpen5e>;
  
  // Destructure state from the table instance
// Cast state to include the sortBy property
const { state: tableState } = tableInstance as TableInstanceWithPlugins<MonsterOpen5e> & {
  state: TableState<MonsterOpen5e> & {
    sortBy: Array<{ id: string; desc: boolean }>;
  };
};

useEffect(() => {
  if (tableState.sortBy && onUpdateSort) {
    onUpdateSort(tableState.sortBy);
  }
}, [tableState.sortBy, onUpdateSort]);
    
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,            // Comes from usePagination
    nextPage,        // Comes from usePagination
    previousPage,    // Comes from usePagination
    canNextPage,     // Comes from usePagination
    canPreviousPage, // Comes from usePagination
    pageOptions,     // Comes from usePagination
    state,           // General state from useTable
    setPageSize,     // Comes from usePagination
    gotoPage,        // Comes from usePagination
  } = tableInstance;

  const paginationState = state as TableStateWithPagination<MonsterOpen5e>;

  useEffect(() => {
    if (currentPage !== null) {
      gotoPage(currentPage);  // Use gotoPage to set the current page
    }
  }, [currentPage]);

  useEffect(() => {
    onUpdateCurrentPage(paginationState.pageIndex);
  }, [paginationState.pageIndex]);

  useEffect(() => {
    onUpdatePageSize(internalPageSize);
  }, [internalPageSize]);

  const handleNameClick = (event: React.MouseEvent<HTMLAnchorElement>, cell: any) => {
    event.preventDefault();
    if (onSelectMonster)
      onSelectMonster(cell.row.original.slug);
  };

  const handleCrChange = (value: string, isMin: boolean) => {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      isMin ? onUpdateMinCr(num) : onUpdateMaxCr(num);
    } else {
      isMin ? onUpdateMinCr(0) : onUpdateMaxCr(0);
    }
  };

  const placeholder = "must match all words, use '-' to exclude words, use \" or ' to surround exact phrases";
  return (
    <div style={{cursor: 'auto'}} id='MonsterGrid'>
      <table>
        <tbody>
          <tr>
            <th colSpan={3} style={{paddingLeft: '10px', paddingRight: '10px'}}>CR range</th>
            <td rowSpan={2} style={{borderLeft: '1px solid grey', paddingLeft: '10px'}}>
              &nbsp;Keywords: <input value={keywords} placeholder={placeholder} onChange={e => onUpdateKeywords(e.target.value)} style={{ width: `500px` }} />
              <PropertySet className='environments' noWrap columns={3} isOpen={envOpen} setIsOpen={setEnvOpen} alwaysShow={true}
                           name="Environments" options={environments} values={selectedEnvironments} onSave={(vals) => onUpdateSelectedEnvironments(vals.split(/, ?/))}
                           onClickOutside={() => setEnvOpen(false)} showClear={true}/>
            </td>
          </tr>
          <tr>
            <td style={{paddingLeft: '10px'}}><input value={minCr || ''} placeholder='min CR' onChange={e => handleCrChange(e.target.value, true)} style={{ width: `50px` }} /></td>
            <td>-</td>
            <td style={{paddingRight: '10px'}}><input value={maxCr || ''} placeholder='max CR' onChange={e => handleCrChange(e.target.value, false)} style={{ width: `50px` }} /></td>
          </tr>
        </tbody>
      </table>

      <table {...getTableProps()} style={{ width: '100%', background: 'white' }}>
        <thead>
          {headerGroups.map(headerGroup => {
            const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
            return (
              <tr key={headerGroupKey} {...headerGroupProps}>
                {headerGroup.headers.map((column, colIndex) => {
                  const sortableColumn = column as unknown as SortableColumnInstance<MonsterOpen5e>;
                  const { key: headerKey, ...headerProps } = sortableColumn.getHeaderProps(sortableColumn.getSortByToggleProps());
                  return (
                    <th key={headerKey} {...headerProps}
                      style={{ width: `${sortableColumn.width}px`, textWrap: 'nowrap', overflow: 'hidden', cursor: 'pointer', borderBottom: '1px solid black',
                               padding: '2px 10px', borderLeft: colIndex > 0 ? '1px solid rgba(0, 0, 0, 0.05)' : undefined}}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <div style={{ flex: 1, textAlign: 'center', marginRight: 'auto', marginLeft: 'auto', width: '100%' }}>
                          {sortableColumn.render('Header')}
                        </div>
                        <span style={{ width: '22px' }}>
                          {sortableColumn.isSorted ? (sortableColumn.isSortedDesc ? '🔽' : '🔼') : ''}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row);
            const { key: rowKey, ...rowProps } = row.getRowProps();
            return (
              <tr key={rowKey} {...rowProps}>
                {row.cells.map((cell, colIndex) => {
                  const { key: cellKey, ...cellProps } = cell.getCellProps();
                  return (
                    <td key={cellKey} {...cellProps}
                      style={{ width: `${cell.column.width}px`, textWrap: 'nowrap', overflow: 'hidden',
                               padding: '2px 10px', borderLeft: colIndex > 0 ? '1px solid rgba(0, 0, 0, 0.05)' : undefined }} >
                      {cell.column.Header === 'Name' ? <a href='#' onClick={(event) => handleNameClick(event, cell)}>{cell.render('Cell')}</a> : cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div> &nbsp; &nbsp; &nbsp;
        <button onClick={previousPage} disabled={!canPreviousPage}>Previous</button>
        <button onClick={nextPage} disabled={!canNextPage}>Next</button> &nbsp; &nbsp;

        <span>
          Page {paginationState.pageIndex + 1} of {pageOptions.length} &nbsp; &nbsp;
        </span>
        <select
          value={internalPageSize}
          onChange={e => {
            const newSize = Number(e.target.value);
            setInternalPageSize(newSize);
            setPageSize(newSize);
          }}
        >
          {[15, 25, 50, 100].map(size => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select> items per page
        <span style={{display: 'block', textAlign: 'right', marginTop: '-15px'}}>
          {loading && <><b style={{color: 'red'}}>Loading...</b> &nbsp; {loadProgress}</>}
          {!loading && monsterData.length !== filteredMonsterData.length && <span>Filtered to {filteredMonsterData.length} out of {monsterData.length} total monsters.</span>}
          {!loading && monsterData.length === filteredMonsterData.length && <span>{monsterData.length} total monsters.</span>}
        </span>
      </div>
    </div>
  );
};

export default MonsterGrid;
